import { useCallback, useMemo, useState } from "react";
import Layout from "../../layout/Layout";
import FiltersPanel from "./components/FiltersPanel";
import RequestsTable from "./components/RequestsTable";
import useModificationRequests from "./hooks/useModificationRequests";
import useAdmin from "./hooks/useAdmin";
import Loading from "../../common/Loading";
import React from "react";
import { useLocation } from "react-router-dom";
import {
  buildRequestedDateText,
  filterRows,
  normalizeOptionValue,
} from "./utils";
import "bootstrap-daterangepicker/daterangepicker.css";

const Page = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialSearch = searchParams.get("search") || searchParams.get("bookingId") || "";

  // Fetch admin users
  const { admins: adminOptions } = useAdmin();
  // Pagination and query params
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');
  const [search, setSearch] = useState(initialSearch);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [requestedDateRange, setRequestedDateRange] = useState(null);
  const [filterTrigger, setFilterTrigger] = useState(0);

  // Build query params for hook
  const queryParams = {
    sortBy,
    search,
    page,
    limit,
    orderBy,
    // Filters as query params
    status: selectedStatus || '',
    userType: selectedUserType || '',
    responseBy: selectedAdmin || '',
    startDate: requestedDateRange?.startDate || '',
    endDate: requestedDateRange?.endDate || '',
    filterTrigger, // force API call when changed
  };

  const { rows, isLoadingRows, requestError, total, hasMore } = useModificationRequests(queryParams);

  const requestedDateText = useMemo(() => buildRequestedDateText(requestedDateRange), [requestedDateRange]);

  const onRequestedDateChange = useCallback((start, end) => {
    setRequestedDateRange({
      startDate: start ? new Date(start).toISOString() : '',
      endDate: end ? new Date(end).toISOString() : '',
    });
    setPage(1);
  }, []);

  const onClearFilters = useCallback(() => {
    setSearch("");
    setSelectedStatus(null);
    setSelectedUserType(null);
    setSelectedAdmin(null);
    setRequestedDateRange(null);
    setPage(1);
  }, []);

  const onClickFilterHandler = () => {
    setFilterTrigger((v) => v + 1);
  }

  // adminOptions now comes from useAdmin hook

  // const filteredRows = useMemo(() => {
  //   return filterRows({
  //     rows,
  //     search,
  //     selectedStatus,
  //     selectedUserType,
  //     selectedAdmin,
  //     requestedDateRange,
  //   });
  // }, [rows, search, selectedStatus, selectedUserType, selectedAdmin, requestedDateRange]);

  // Pagination controls
  const totalPages = Math.ceil(total / limit);
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  // Next is disabled if !hasMore
  const handleNext = () => {
    if (hasMore) setPage((p) => p + 1);
  };
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100" >
        <div className="tw:font-hanken-grotesk tw:text-[#212529]">
          <div className="tw:pt-[34px]">
            <div className="tw:mb-0 tw:font-hanken-grotesk tw:text-[22px] tw:font-semibold tw:leading-[normal] tw:text-[#212529]">
              Modification Requests
            </div>
            <div className="tw:mt-[34px] tw:h-px tw:w-full tw:bg-[#E6E6E6]" />
          </div>

          <FiltersPanel
            search={search}
            setSearch={setSearch}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedUserType={selectedUserType}
            setSelectedUserType={setSelectedUserType}
            selectedAdmin={selectedAdmin}
            setSelectedAdmin={setSelectedAdmin}
            adminOptions={adminOptions}
            requestedDateRange={requestedDateRange}
            requestedDateText={requestedDateText}
            onRequestedDateChange={onRequestedDateChange}
            onClearFilters={onClearFilters}
            filterHandler={onClickFilterHandler}
          />

          {isLoadingRows ? (
            <Loading />
          ) : (
            <RequestsTable
              rows={rows}
              isLoadingRows={isLoadingRows}
              requestError={requestError}
              orderBy={orderBy}
              sortBy={sortBy}
              onSort={(field) => {
                if (orderBy === field) {
                  setSortBy((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                } else {
                  setOrderBy(field);
                  setSortBy('desc');
                }
              }}
            />
          )}

          {/* Pagination Controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 24 }}>
            <button onClick={handlePrev} disabled={page === 1} style={{ marginRight: 8 }}>&lt; Prev</button>
            <span style={{ margin: '0 8px' }}>Page {page} of {totalPages || 1}</span>
            <button onClick={handleNext} disabled={!hasMore} style={{ marginLeft: 8 }}>Next &gt;</button>
            <span style={{ marginLeft: 16 }}>Rows per page: </span>
            <select value={limit} onChange={handleLimitChange} style={{ marginLeft: 4 }}>
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default Page;
